import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Truck, Award, Upload, Download, Paperclip } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { SupplierNcHistoryCard } from '../components/suppliers/SupplierNcHistoryCard';
import { supplierService, supplierCertificationService, attachmentService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CertificationStatusBadge, { CERT_TYPES } from '../components/CertificationStatusBadge';

const SUPPLIER_TYPE_LABELS = {
  raw_material: 'Materia Prima',
  treatment: 'Trattamento',
  machining: 'Lavorazione',
  other: 'Altro',
};

const emptyCert = {
  certification_type: 'ISO 9001',
  certificate_number: '',
  certification_body: '',
  issue_date: '',
  expiry_date: '',
  notes: '',
  attachment_url: '',
  attachment_name: '',
};

export default function SupplierDetailPage() {
  const { id } = useParams();
  const { hasRole } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCert);
  const [pendingFile, setPendingFile] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        supplierService.getById(id),
        supplierCertificationService.getBySupplier(id),
      ]);
      setSupplier(s.data);
      setCerts(c.data || []);
    } catch {
      toast.error('Errore caricamento fornitore');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyCert);
    setPendingFile(null);
    setDialogOpen(true);
  };

  const openEdit = (cert) => {
    setEditing(cert);
    setForm({
      certification_type: cert.certification_type || 'ISO 9001',
      certificate_number: cert.certificate_number || '',
      certification_body: cert.certification_body || '',
      issue_date: cert.issue_date || '',
      expiry_date: cert.expiry_date || '',
      notes: cert.notes || '',
      attachment_url: cert.attachment_url || '',
      attachment_name: cert.attachment_name || '',
    });
    setPendingFile(null);
    setDialogOpen(true);
  };

  const validateFile = (file) => {
    const ok = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!ok.includes(file.type)) {
      toast.error('Solo PDF o immagini ammessi');
      return false;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File troppo grande (max 50MB)');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!form.certification_type) {
      toast.error('Seleziona il tipo certificazione');
      return;
    }
    try {
      const payload = {
        certification_type: form.certification_type,
        certificate_number: form.certificate_number || null,
        certification_body: form.certification_body || null,
        issue_date: form.issue_date || null,
        expiry_date: form.expiry_date || null,
        notes: form.notes || null,
        attachment_url: form.attachment_url || null,
        attachment_name: form.attachment_name || null,
      };
      let cert;
      if (editing) {
        const r = await supplierCertificationService.update(editing.id, payload);
        cert = r.data;
      } else {
        const r = await supplierCertificationService.create(id, payload);
        cert = r.data;
      }

      // Upload attachment if any
      if (pendingFile && cert?.id) {
        const r = await attachmentService.upload(pendingFile, 'supplier_certification', cert.id, 'certification', form.certification_type);
        const att = r.data;
        await supplierCertificationService.update(cert.id, {
          attachment_url: att.file_url,
          attachment_name: att.file_name,
        });
      }

      toast.success(editing ? 'Certificazione aggiornata' : 'Certificazione creata');
      setDialogOpen(false);
      setPendingFile(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await supplierCertificationService.delete(deleteId);
      toast.success('Certificazione eliminata');
      setDeleteId(null);
      load();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const openAttachment = (url) => {
    if (!url) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API_URL}${url}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  if (!supplier) {
    return <div className="text-center py-12 text-slate-500">Fornitore non trovato</div>;
  }

  const expiringCount = certs.filter(c => c.status === 'in_scadenza').length;
  const expiredCount = certs.filter(c => c.status === 'scaduta').length;
  const validCount = certs.filter(c => c.status === 'valida').length;

  return (
    <div className="space-y-6" data-testid="supplier-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/suppliers">
            <Button variant="ghost" size="sm" className="h-10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <Truck className="w-6 h-6 text-slate-700" />
              <h1 className="text-2xl font-bold text-slate-900 font-mono">{supplier.code}</h1>
              <Badge className="bg-slate-100 text-slate-700 border border-slate-300">
                {SUPPLIER_TYPE_LABELS[supplier.supplier_type] || supplier.supplier_type}
              </Badge>
              <Badge className={supplier.is_active ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-slate-100 text-slate-500 border border-slate-300'}>
                {supplier.is_active ? 'Attivo' : 'Disattivato'}
              </Badge>
            </div>
            <p className="text-slate-600 text-base mt-1">{supplier.name}</p>
          </div>
        </div>
      </div>

      {/* Info card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Anagrafica</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Referente</p>
              <p className="mt-1">{supplier.contact_person || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Email</p>
              <p className="mt-1">{supplier.email || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Telefono</p>
              <p className="mt-1">{supplier.phone || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Certifications summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Totale Certificazioni</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{certs.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-5">
            <p className="text-xs text-emerald-600 uppercase tracking-wider font-bold">Valide</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">{validCount}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-5">
            <p className="text-xs text-amber-700 uppercase tracking-wider font-bold">In Scadenza</p>
            <p className="text-3xl font-bold text-amber-600 mt-1">{expiringCount}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-5">
            <p className="text-xs text-red-600 uppercase tracking-wider font-bold">Scadute</p>
            <p className="text-3xl font-bold text-red-600 mt-1">{expiredCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Certifications table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Award className="w-5 h-5 text-slate-700" />
            Certificazioni Fornitore
          </CardTitle>
          {hasRole('admin', 'quality', 'production') && (
            <Button onClick={openCreate} className="h-10 px-4" data-testid="add-certification-btn">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Certificazione
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {certs.length === 0 ? (
            <p className="text-center text-slate-500 py-8 italic">Nessuna certificazione registrata</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Tipo</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Numero</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Ente</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Emissione</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Scadenza</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Stato</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Allegato</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map(c => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`cert-row-${c.id}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">{c.certification_type}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">{c.certificate_number || '-'}</td>
                      <td className="px-3 py-2">{c.certification_body || '-'}</td>
                      <td className="px-3 py-2">{c.issue_date || '-'}</td>
                      <td className="px-3 py-2 font-medium">{c.expiry_date || '-'}</td>
                      <td className="px-3 py-2"><CertificationStatusBadge status={c.status} days={c.days_to_expiry} /></td>
                      <td className="px-3 py-2">
                        {c.attachment_url ? (
                          <button onClick={() => openAttachment(c.attachment_url)} className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-800" data-testid={`open-cert-attachment-${c.id}`}>
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[140px]">{c.attachment_name || 'allegato'}</span>
                          </button>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {hasRole('admin', 'quality', 'production') && (
                          <>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(c)} data-testid={`edit-cert-${c.id}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteId(c.id)} data-testid={`delete-cert-${c.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Storico Non Conformità Fornitore */}
      <SupplierNcHistoryCard supplierId={id} />

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica Certificazione' : 'Nuova Certificazione'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo Certificazione *</Label>
                <Select value={form.certification_type} onValueChange={(v) => setForm(f => ({ ...f, certification_type: v }))}>
                  <SelectTrigger data-testid="cert-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CERT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Numero Certificato</Label>
                <Input value={form.certificate_number} onChange={(e) => setForm(f => ({ ...f, certificate_number: e.target.value }))} placeholder="es. IT-2024-001" data-testid="cert-number-input" />
              </div>
              <div className="space-y-2">
                <Label>Ente Certificatore</Label>
                <Input value={form.certification_body} onChange={(e) => setForm(f => ({ ...f, certification_body: e.target.value }))} placeholder="es. DNV, Bureau Veritas, TUV" data-testid="cert-body-input" />
              </div>
              <div className="space-y-2">
                <Label>Data Emissione</Label>
                <Input type="date" value={form.issue_date} onChange={(e) => setForm(f => ({ ...f, issue_date: e.target.value }))} data-testid="cert-issue-date" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Data Scadenza</Label>
                <Input type="date" value={form.expiry_date} onChange={(e) => setForm(f => ({ ...f, expiry_date: e.target.value }))} data-testid="cert-expiry-date" />
                <p className="text-xs text-slate-500">Lo stato (Valida / In scadenza / Scaduta) viene calcolato automaticamente. Soglia: 60 giorni.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Annotazioni" data-testid="cert-notes" />
            </div>

            {/* Attachment */}
            <div className="space-y-2">
              <Label>Allegato (PDF o Immagine)</Label>
              {form.attachment_url && !pendingFile && (
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                  <Paperclip className="w-4 h-4 text-slate-500" />
                  <button type="button" onClick={() => openAttachment(form.attachment_url)} className="text-sky-600 hover:text-sky-800 flex-1 text-left text-sm truncate">{form.attachment_name || 'allegato attuale'}</button>
                  <Button type="button" variant="ghost" size="sm" className="h-8 text-red-500" onClick={() => setForm(f => ({ ...f, attachment_url: '', attachment_name: '' }))}>
                    Rimuovi
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && validateFile(f)) setPendingFile(f);
                  }}
                  data-testid="cert-file-input"
                />
                {pendingFile && (
                  <Badge className="bg-sky-100 text-sky-700 border border-sky-300 whitespace-nowrap">
                    <Upload className="w-3 h-3 mr-1" />
                    {pendingFile.name}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSubmit} data-testid="cert-submit-btn">{editing ? 'Aggiorna' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Conferma eliminazione</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 py-2">Vuoi eliminare definitivamente questa certificazione? L'operazione non e reversibile.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-cert">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
